import { useEffect, useRef, useState, useCallback } from 'react';
import { resolveAuthToken, bearerAuthHeader } from '/src/utils/auth';

const FULLSCREEN_CLASSES = 'fixed top-0 left-0 w-screen h-screen z-[9999] border-none';
const EMBEDDED_CLASSES = 'w-full h-full border-none rounded-xl';

function getIframeSrc() {
  const base = `${window.CORE_API_URL}/today`;
  return window.ENVIRONMENT !== 'development' ? `${base}?v=${window.TODAY_VIEW_VERSION}` : base;
}

export function TodayView({ site }) {
  const iframeRef = useRef(null);
  const siteRef = useRef(site);
  const listenerAttached = useRef(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  siteRef.current = site;

  useEffect(() => {
    const allowedOrigin = new URL(window.CORE_API_URL).origin;

    function handleMessage(event) {
      if (event.origin !== allowedOrigin) return;
      if (event.source !== iframeRef.current?.contentWindow) return;

      if (event.data?.subject === 'today-view:ready') {
        const token = resolveAuthToken();
        const header = bearerAuthHeader(token);
        iframeRef.current?.contentWindow?.postMessage(
          { subject: 'today-view:auth-token', authorization: header.authorization },
          allowedOrigin,
        );
      }

      if (event.data?.subject === 'today-view:request-site' && siteRef.current) {
        iframeRef.current?.contentWindow?.postMessage(
          { subject: 'today-view:site', site: { id: siteRef.current.id, name: siteRef.current.name } },
          allowedOrigin,
        );
      }

      if (event.data?.subject === 'today-view:set-fullscreen') {
        setIsFullScreen(event.data.fullScreen);
      }
    }

    window.addEventListener('message', handleMessage);
    listenerAttached.current = true;

    // Set src after the listener is attached to avoid missing the ready message
    if (iframeRef.current && !iframeRef.current.src) {
      iframeRef.current.src = getIframeSrc();
    }

    return () => {
      listenerAttached.current = false;
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Set src when the iframe element mounts, if the listener is already attached
  const iframeCallbackRef = useCallback((el) => {
    iframeRef.current = el;
    if (el && listenerAttached.current && !el.src) {
      el.src = getIframeSrc();
    }
  }, []);

  // Push site changes to the iframe
  const siteId = site?.id;
  useEffect(() => {
    if (!siteId || !iframeRef.current) return;
    const current = siteRef.current;
    const allowedOrigin = new URL(window.CORE_API_URL).origin;
    iframeRef.current.contentWindow?.postMessage(
      { subject: 'today-view:site', site: { id: current.id, name: current.name } },
      allowedOrigin,
    );
  }, [siteId]);

  return (
    <iframe
      ref={iframeCallbackRef}
      title="Today View"
      className={isFullScreen ? FULLSCREEN_CLASSES : EMBEDDED_CLASSES}
    />
  );
}
